import { keepPreviousData, queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  aiEditPersonaBriefs,
  deletePersonaBriefs,
  fetchPersonaBriefVersionHistory,
  fetchPersonaOpportunitySetBriefs,
  patchPersonaBriefContent,
  restorePersonaBriefVersion,
  togglePersonaBriefFavorite,
  type PersonaBriefAiEditCell,
  type PersonaBriefContentPatch,
  type PersonaBriefDto,
} from "@/features/persona-opportunities/api/persona-opportunity-briefs.api";
import type { StatusResult } from "@/features/identity-hub/api/identity-hub.api";
import i18n from "@/lib/i18n";
import { getLocalUserFacingApiMessage } from "@/lib/api-client";
import { Sentry } from "@/lib/sentry";
import { useAmberMutation } from "@/lib/use-amber-mutation";

import { revealGenerationTask } from "./opportunity-sets.queries";
import {
  normalizedPersonaOpportunitySetTenantId,
  personaOpportunitySetKeys,
} from "./persona-opportunity-sets.queries";

export const personaOpportunityBriefKeys = {
  all: [...personaOpportunitySetKeys.all, "briefs"] as const,
  bySet: (tenantId: string, setId: string) =>
    [...personaOpportunityBriefKeys.all, "set", tenantId, setId] as const,
  versions: (tenantId: string, setId: string, briefId: string) =>
    [...personaOpportunityBriefKeys.bySet(tenantId, setId), "versions", briefId] as const,
  version: (tenantId: string, setId: string, briefId: string, version: number) =>
    [...personaOpportunityBriefKeys.versions(tenantId, setId, briefId), version] as const,
} as const;

export function personaOpportunitySetBriefsQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  options: { enabled?: boolean; refetchInterval?: number | false } = {},
) {
  const enabled = options.enabled ?? true;
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityBriefKeys.bySet(normalizedTenant, setId),
    queryFn: (): Promise<PersonaBriefDto[]> => fetchPersonaOpportunitySetBriefs(setId),
    enabled: !!tenantId && !!setId && enabled,
    refetchInterval: options.refetchInterval ?? false,
    meta: { skipGlobalError: true },
  });
}

export function personaBriefVersionHistoryQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  briefId: string,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  return queryOptions({
    queryKey: personaOpportunityBriefKeys.versions(normalizedTenant, setId, briefId),
    queryFn: () => fetchPersonaBriefVersionHistory(setId, briefId),
    enabled: !!tenantId && !!setId && !!briefId && (options.enabled ?? true),
  });
}

export function personaBriefVersionQueryOptions(
  tenantId: string | null | undefined,
  setId: string,
  briefId: string,
  version: number | null,
  options: { enabled?: boolean } = {},
) {
  const normalizedTenant = normalizedPersonaOpportunitySetTenantId(tenantId);
  const resolvedVersion = version ?? 0;
  return queryOptions({
    queryKey: personaOpportunityBriefKeys.version(
      normalizedTenant,
      setId,
      briefId,
      resolvedVersion,
    ),
    queryFn: () => fetchPersonaBriefVersionHistory(setId, briefId, resolvedVersion),
    placeholderData: keepPreviousData,
    enabled:
      !!tenantId &&
      !!setId &&
      !!briefId &&
      version != null &&
      (options.enabled ?? true),
  });
}

export interface PatchPersonaBriefContentVariables {
  setId: string;
  briefId: string;
  tenantId: string;
  patch: PersonaBriefContentPatch;
}

/** Sync persist of a manual title, cell, dropdown, or brand edit. */
export function usePatchPersonaBriefContent() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, briefId, tenantId, patch }: PatchPersonaBriefContentVariables) =>
      patchPersonaBriefContent(setId, briefId, tenantId, patch),
    onSuccess: async (updated, variables) => {
      const normalizedTenant = normalizedPersonaOpportunitySetTenantId(variables.tenantId);
      queryClient.setQueryData<PersonaBriefDto[]>(
        personaOpportunityBriefKeys.bySet(normalizedTenant, variables.setId),
        (current) =>
          current?.map((brief) => (brief.id === updated.id ? updated : brief)) ?? current,
      );
      await queryClient.invalidateQueries({ queryKey: personaOpportunityBriefKeys.all });
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

export interface TogglePersonaBriefFavoriteVariables {
  setId: string;
  briefId: string;
  tenantId: string;
  isFavorite: boolean;
}

interface TogglePersonaBriefFavoriteContext {
  key: readonly unknown[];
  previous: PersonaBriefDto[] | undefined;
}

/** Optimistic persist of the brief identity star. */
export function useTogglePersonaBriefFavorite() {
  const queryClient = useQueryClient();

  return useMutation<
    { id: string; isFavorite: boolean },
    Error,
    TogglePersonaBriefFavoriteVariables,
    TogglePersonaBriefFavoriteContext
  >({
    mutationFn: ({ setId, briefId, tenantId, isFavorite }) =>
      togglePersonaBriefFavorite(setId, briefId, tenantId, isFavorite),
    onMutate: async ({ setId, tenantId, briefId, isFavorite }) => {
      const key = personaOpportunityBriefKeys.bySet(
        normalizedPersonaOpportunitySetTenantId(tenantId),
        setId,
      );
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<PersonaBriefDto[]>(key);
      queryClient.setQueryData<PersonaBriefDto[]>(key, (current) =>
        current?.map((brief) => (brief.id === briefId ? { ...brief, isFavorite } : brief)) ??
        current,
      );
      return { key, previous };
    },
    onError: (err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(context.key, context.previous);
      }
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    onSettled: (_data, _err, variables) => {
      void queryClient.invalidateQueries({
        queryKey: personaOpportunityBriefKeys.bySet(
          normalizedPersonaOpportunitySetTenantId(variables.tenantId),
          variables.setId,
        ),
      });
    },
    meta: { skipGlobalError: true },
  });
}

export interface RestorePersonaBriefVersionVariables {
  setId: string;
  briefId: string;
  version: number;
  tenantId: string;
}

export function useRestorePersonaBriefVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      setId,
      briefId,
      version,
      tenantId,
    }: RestorePersonaBriefVersionVariables) =>
      restorePersonaBriefVersion(setId, briefId, version, tenantId),
    onSuccess: async (_updated, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personaOpportunityBriefKeys.all }),
        queryClient.invalidateQueries({
          queryKey: personaOpportunityBriefKeys.versions(
            normalizedPersonaOpportunitySetTenantId(variables.tenantId),
            variables.setId,
            variables.briefId,
          ),
        }),
      ]);
      toast.success(
        i18n.t("personaOpportunities.scripts.briefs.history.restored", {
          defaultValue: "Restored version {{version}}",
          version: variables.version,
        }),
      );
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}

const PERSONA_AI_EDIT_POLL_MAX_ATTEMPTS = 60;
const PERSONA_AI_EDIT_POLL_INTERVAL_MS = 1_000;

export interface AiEditPersonaBriefsVariables {
  setId: string;
  briefIds: string[];
  instruction: string;
  tenantId: string;
  idempotencyKey: string;
  cell?: PersonaBriefAiEditCell;
}

function countPersonaAiEditResults(result: StatusResult): {
  successCount: number;
  errorCount: number;
} {
  const rows = result.results;
  if (!Array.isArray(rows)) return { successCount: 0, errorCount: 0 };
  let successCount = 0;
  let errorCount = 0;
  for (const entry of rows) {
    if (!entry || typeof entry !== "object") continue;
    const status = (entry as { status?: unknown }).status;
    if (status === "success") successCount += 1;
    if (status === "error") errorCount += 1;
  }
  return { successCount, errorCount };
}

export function useAiEditPersonaBriefs() {
  const queryClient = useQueryClient();

  return useAmberMutation<AiEditPersonaBriefsVariables>({
    mutationFn: async ({ setId, briefIds, instruction, tenantId, idempotencyKey, cell }) => {
      const result = await aiEditPersonaBriefs({
        setId,
        briefIds,
        instruction,
        tenantId,
        idempotencyKey,
        cell,
      });
      revealGenerationTask(queryClient);
      return result;
    },
    pollBasePath: "/persona-opportunity-sets",
    invalidateKeys: [],
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: personaOpportunityBriefKeys.all,
      });
      if (result.cancelled === true) {
        const { successCount } = countPersonaAiEditResults(result);
        toast.info(
          i18n.t("personaOpportunities.scripts.briefs.aiEditor.cancelled", {
            defaultValue: "Edit cancelled — {{count}} brief(s) saved",
            count: successCount,
          }),
        );
        return;
      }
      const { successCount, errorCount } = countPersonaAiEditResults(result);
      if (errorCount > 0 && successCount === 0) {
        toast.error(
          i18n.t("personaOpportunities.scripts.briefs.aiEditor.allFailed", {
            defaultValue: "Could not update the selected briefs",
          }),
        );
        return;
      }
      if (errorCount > 0) {
        toast.warning(
          i18n.t("personaOpportunities.scripts.briefs.aiEditor.partialSuccess", {
            defaultValue: "Updated {{successCount}} briefs; {{errorCount}} failed",
            successCount,
            errorCount,
          }),
        );
        return;
      }
      if (variables.cell) {
        toast.success(
          i18n.t("personaOpportunities.scripts.cell.aiUpdated", {
            defaultValue: "Cell updated",
          }),
        );
        return;
      }
      toast.success(
        i18n.t("personaOpportunities.scripts.briefs.aiEditor.success", {
          defaultValue: "Brief updated successfully",
        }),
      );
    },
    maxAttempts: PERSONA_AI_EDIT_POLL_MAX_ATTEMPTS,
    intervalMs: PERSONA_AI_EDIT_POLL_INTERVAL_MS,
  });
}

export interface DeletePersonaBriefsVariables {
  setId: string;
  tenantId: string;
  briefIds: string[];
}

/** Soft-delete selected briefs; invalidates list + set counts. */
export function useDeletePersonaBriefs() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ setId, tenantId, briefIds }: DeletePersonaBriefsVariables) =>
      deletePersonaBriefs(setId, {
        tenantId,
        idempotencyKey: crypto.randomUUID(),
        briefIds,
      }),
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: personaOpportunityBriefKeys.all }),
        queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.all }),
      ]);
      if (result.deletedIds.length === 0) return;
      toast.success(
        i18n.t("personaOpportunities.scripts.briefs.bulk.deleteToast", {
          count: result.deletedIds.length,
          defaultValue_one: "{{count}} brief deleted",
          defaultValue_other: "{{count}} briefs deleted",
        }),
      );
    },
    onError: (err) => {
      Sentry.captureException(err);
      const message = getLocalUserFacingApiMessage(err);
      if (message) toast.error(message);
    },
    meta: { skipGlobalError: true },
  });
}
